import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState, DocumentTemplatesActions } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@qtek/shared/components';
import { DocumentTemplate, TemplateTypes } from '@qtek/shared/models';
import { DEFAULT_QUILL_MODULES } from '@qtek/shared/utils';
import { QuillModule } from 'ngx-quill';
import { take } from 'rxjs/operators';
import { adjustFoldersForPayload } from '../../utils/adjust-folder-for-payload';
import {
  FolderNode,
  FolderStructureBuilderComponent,
} from '../folder-structure-builder';
import { templateNameExistsValidator } from './template-name.validator';

export const createTemplateDialogConfig: MatDialogConfig = {
  width: 'min(1200px, 90vw)',
  height: 'min(700px, 90vh)',
  autoFocus: false,
};

export interface CreateTemplateDialogData {
  existingTemplates?: string[];
  templateType?: 'rtf' | 'docs';
}

@Component({
  selector: 'qt-create-template-dialog',
  templateUrl: './create-template-dialog.component.html',
  styleUrls: ['./create-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslationCoreModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    QuillModule,
    MatProgressSpinnerModule,
    FolderStructureBuilderComponent,
  ],
})
export class CreateTemplateDialogComponent implements OnInit, OnDestroy {
  private data = inject<CreateTemplateDialogData>(MAT_DIALOG_DATA);
  private store = inject(Store<AppState>);
  private dialogRef = inject(MatDialogRef<CreateTemplateDialogComponent>);
  private actions$ = inject(Actions);
  private destroyRef = inject(DestroyRef);

  templateForm: FormGroup = new FormGroup({
    name: new FormControl<string>('', [
      Validators.required,
      templateNameExistsValidator(this.data.existingTemplates || []),
    ]),
    content: new FormControl<string>(''),
  });

  isCreating = false;
  createdTemplate: DocumentTemplate | null = null;
  quillModules = DEFAULT_QUILL_MODULES;
  folderStructure: FolderNode[] = [];

  get nameControl(): FormControl<string> {
    return this.templateForm.get('name') as FormControl<string>;
  }

  get contentControl(): FormControl<string> {
    return this.templateForm.get('content') as FormControl<string>;
  }

  get templateType(): string {
    return this.data.templateType || 'rtf';
  }

  onFolderStructureChanged(structure: FolderNode[]): void {
    this.folderStructure = structure;
    const filteredStructure = adjustFoldersForPayload(structure);
    this.templateForm.patchValue({
      content: JSON.stringify(filteredStructure),
    });
  }

  ngOnInit(): void {
    if (this.templateType === 'rtf') {
      this.contentControl.setValidators([Validators.required]);
    } else {
      this.contentControl.clearValidators();
    }
    this.contentControl.updateValueAndValidity();

    this.createTemplateData();
    this.connectToTemplate();
    this.updateTemplateUpdate();
  }

  ngOnDestroy(): void {
    this.store.dispatch(DocumentTemplatesActions.disconnectFromTemplate());
  }

  createAndPublishTemplate(): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.isCreating = true;
    const formValue = this.templateForm.value;

    const documentTemplateType =
      this.templateType === 'rtf' ? TemplateTypes.RTF : TemplateTypes.FOLDER;

    this.store.dispatch(
      DocumentTemplatesActions.createDocumentTemplates({
        documentTemplateName: formValue.name,
        documentTemplateType,
      })
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private createTemplateData() {
    this.actions$
      .pipe(
        ofType(DocumentTemplatesActions.queryDocumentTemplatesSuccess),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ documentTemplates }) => {
        const createdTemplate = documentTemplates.find(
          template => template.name === this.nameControl.value
        );

        this.store.dispatch(
          DocumentTemplatesActions.createDocumentTemplateData({
            tplId: createdTemplate._id,
          })
        );
      });
  }

  private connectToTemplate(): void {
    this.actions$
      .pipe(
        ofType(DocumentTemplatesActions.createDocumentTemplatesSuccess),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ documentTemplate }) => {
        this.createdTemplate = documentTemplate;
        this.store.dispatch(
          DocumentTemplatesActions.connectToTemplate({
            payload: {
              view: 'dtl',
              id: documentTemplate._id,
              mysid: '*tpl',
              s: 'model',
            },
          })
        );
      });
  }

  private updateTemplateUpdate() {
    this.actions$
      .pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesSuccess),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ payload: createdTemplate }) => {
        this.publishTemplateData(createdTemplate);
      });

    this.actions$
      .pipe(
        ofType(DocumentTemplatesActions.updateDocumentTemplatesFailure),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isCreating = false;
      });
  }

  private publishTemplateData(template: DocumentTemplate): void {
    const filteredStructure = adjustFoldersForPayload(this.folderStructure);
    const content =
      this.templateType === 'docs'
        ? JSON.stringify(filteredStructure)
        : this.templateForm.get('content')?.value || '';

    const tplData = template.dataMeta.templateData[0];

    this.store.dispatch(
      DocumentTemplatesActions.updateDocumentTemplatesData({
        id: `${tplData.id}.${tplData.vts}`,
        res: {
          _vsg: 'published',
          bdy: content,
          tpl: {
            recipients: [],
          },
        },
      })
    );

    this.isCreating = false;
    this.dialogRef.close(template);
  }
}
